import React, { FC, useState } from 'react';
import CustomTextField from '../../../common/components/CustomTextField';
import {
  CreateEnseigneInput,
  useCreateEnseign,
  useUpdateEnseign,
} from '../../../hooks/mutations/useEnseign';
import CustomSelect from '../../../common/components/CustomSelect';
import {
  PhysicalModel,
  TypeEnseigne,
} from '../../../types/graphql-global-types';
import { displaySnackBar } from '../../../common/displaySnackbar';
import {
  deformatSIRETSIREN,
  formatSIREN,
  isEmailValid as validateEmail,
  isSIREN,
} from '../../../common/validator';
import CustomPhoneInput from '../../../common/components/CustomPhoneInput';
import { isValidPhoneNumber } from 'react-phone-number-input';
import ConfirmationButtons from '../../ConfirmationButtons/ConfirmationButtons';
import { Divider, Grid, Typography } from '@material-ui/core';
import { TYPE_ENSEIGN } from '../../../common/constants';
import CustomDialog from '../../../common/components/CustomDialog';
import { CustomDialogProps } from '../../../common/components/CustomDialog/CustomDialog';
import { AllEnseignes_allEnseignes_enseignes } from '../../../graphql/enseigne/types/AllEnseignes';

interface IEnseigneFormsModal {
  onCancel: () => void;
  onCreate: () => void;
  setIsLoading: (isLoading: boolean) => void;
  selectedEnseigne?: AllEnseignes_allEnseignes_enseignes;
  open?: boolean;
}

type EnseigneStateType = CreateEnseigneInput;

const initialEnseign: EnseigneStateType = {
  BIC: '',
  SIREN: '',
  IBAN: '',
  city: '',
  email: '',
  name: '',
  phone: '',
  type: null,
  zipCode: '',
  address: '',
  country: '',
  physicalModel: null,
  socialReason: '',
  entityIds: null,
};

type InputType = {
  type: 'text' | 'select' | 'tel' | 'email' | 'number' | 'autoComplete';
  label: string;
  value: string | number[] | null;
  key: keyof EnseigneStateType;
  required?: boolean;
  helperText?: string;
  onBlur?: () => void;
  error?: boolean;
};

const EnseigneFormsModal: FC<IEnseigneFormsModal & CustomDialogProps> = (
  props,
) => {
  const { onCancel, onCreate, setIsLoading, selectedEnseigne } = props;
  const initialEnseignState: EnseigneStateType = selectedEnseigne
    ? generateInitialEnseigne(selectedEnseigne)
    : initialEnseign;

  const [isValidSIREN, setIsValidSIREN] = useState<boolean>(true);
  const [enseign, setEnseign] = useState<EnseigneStateType>(
    initialEnseignState,
  );
  const [isFinishFillInputs, setIsFinishFillInputs] = useState<boolean>(false);
  const [isEmailValid, setIsEmailValid] = useState<boolean>(true);
  // const [isWithoutAgency, setIsWithoutAgency] = useState<boolean>(false);

  const [
    createEnseign,
    { loading: createLoading, client },
  ] = useCreateEnseign();
  const [updateEnseign, { loading: updateLoading }] = useUpdateEnseign();

  const inputs: InputType[] = generateInputs(enseign);

  const typeOptions: { label: string; value: string }[] = [
    {
      label: TYPE_ENSEIGN[TypeEnseigne.CONNECTCREDIT],
      value: TypeEnseigne.CONNECTCREDIT,
    },
    {
      label: TYPE_ENSEIGN[TypeEnseigne.REAL_ESTATE_TRANSACTION],
      value: TypeEnseigne.REAL_ESTATE_TRANSACTION,
    },
    {
      label: TYPE_ENSEIGN[TypeEnseigne.REAL_ESTATE_PROMOTION],
      value: TypeEnseigne.REAL_ESTATE_PROMOTION,
    },
    {
      label: TYPE_ENSEIGN[TypeEnseigne.HERITAGE_MANAGEMENT],
      value: TypeEnseigne.HERITAGE_MANAGEMENT,
    },
    {
      label: TYPE_ENSEIGN[TypeEnseigne.NOTARIAT],
      value: TypeEnseigne.NOTARIAT,
    },
    { label: TYPE_ENSEIGN[TypeEnseigne.BANK], value: TypeEnseigne.BANK },
    {
      label: TYPE_ENSEIGN[TypeEnseigne.INSURANCE],
      value: TypeEnseigne.INSURANCE,
    },
    {
      label: TYPE_ENSEIGN[TypeEnseigne.PARRAINAGE],
      value: TypeEnseigne.PARRAINAGE,
    },
    { label: TYPE_ENSEIGN[TypeEnseigne.OTHER], value: TypeEnseigne.OTHER },
  ];

  const PhysicalModelOptions: { label: string; value: string }[] = [
    { label: 'Avec agence(s)', value: PhysicalModel.WITH_AGENCY },
    { label: 'Sans agence', value: PhysicalModel.WITHOUT_AGENCY },
  ];

  const canUpsertEnseign =
    !inputs.filter((input) => input?.required && !input?.value)?.length &&
    isEmailValid &&
    isValidSIREN;

  const handleChange = (key: string, value: string) => {
    if (key in enseign) {
      setEnseign((prevState) => ({ ...prevState, [key]: value }));
    }
  };

  const handleCreate = () => {
    const {
      name,
      BIC,
      IBAN,
      SIREN,
      city,
      email,
      phone,
      type,
      zipCode,
      country,
      address,
      socialReason,
      physicalModel,
      entityIds,
    } = enseign;
    if (canUpsertEnseign) {
      setIsLoading(true);
      createEnseign({
        variables: {
          input: {
            name,
            BIC,
            IBAN,
            SIREN: deformatSIRETSIREN(SIREN || ''),
            city,
            email,
            phone,
            type,
            zipCode,
            country,
            address,
            socialReason,
            physicalModel,
            entityIds,
          },
        },
      }).then((result) => {
        if (result && result.data && result.data?.createEnseigne) {
          displaySnackBar(client, {
            type: 'SUCCESS',
            message: 'Ajouté avec succès',
            isOpen: true,
          });
        }
        onCreate();
        setIsLoading(false);
      });
    }
  };

  const handleUpdate = () => {
    const {
      name,
      BIC,
      IBAN,
      SIREN,
      city,
      email,
      phone,
      type,
      zipCode,
      address,
      country,
      socialReason,
      physicalModel,
      entityIds,
    } = enseign;
    if (selectedEnseigne && canUpsertEnseign) {
      setIsLoading(true);
      updateEnseign({
        variables: {
          input: {
            id: selectedEnseigne?.id,
            name,
            BIC,
            IBAN,
            SIREN: deformatSIRETSIREN(SIREN || ''),
            city,
            email,
            phone,
            type,
            zipCode,
            address,
            country,
            physicalModel,
            socialReason,
            entityIds,
          },
        },
      }).then((result) => {
        if (result && result.data && result.data?.updateEnseigne) {
          displaySnackBar(client, {
            type: 'SUCCESS',
            message: 'Modifié avec succès',
            isOpen: true,
          });
        }
        onCreate();
        setIsLoading(false);
      });
    }
  };

  const handleSIRENBlur = () => {
    setIsValidSIREN(isSIREN(deformatSIRETSIREN(enseign.SIREN || '')));
  };

  return (
    <CustomDialog
      {...props}
      loading={!!props?.loading || createLoading || updateLoading}
      actions={
        <ConfirmationButtons
          handleCancel={onCancel}
          handleConfirm={() => {
            selectedEnseigne ? handleUpdate() : handleCreate();
            setIsFinishFillInputs(true);
          }}
          disableConfirm={createLoading || updateLoading}
          confirmText={createLoading ? 'Chargement' : undefined}
        />
      }>
      <Grid container spacing={1}>
        {inputs.map((input) => {
          return (
            <Grid
              item
              key={input.key}
              xs={12}
              sm={input.key === 'socialReason' ? 12 : 6}>
              {input.key === 'socialReason' && (
                <>
                  <Typography variant="h6">Siège</Typography>
                  <Divider />
                </>
              )}
              {(input.type === 'text' ||
                input.type === 'email' ||
                input.type === 'number') && (
                <CustomTextField
                  type={input.type}
                  label={input.label}
                  value={
                    input?.key === 'SIREN'
                      ? formatSIREN(input.value as string)
                      : input.value
                  }
                  required={input.required}
                  onBlur={() => {
                    if (input.type === 'email') {
                      setIsEmailValid(
                        validateEmail(
                          (input?.value as string) || 'example@email.com',
                        ),
                      );
                    }
                  }}
                  error={
                    (isFinishFillInputs && input?.required && !input?.value) ||
                    (input.type === 'email' && !isEmailValid) ||
                    (input.key === 'SIREN' && !isValidSIREN)
                  }
                  helperText={
                    (isFinishFillInputs &&
                      input?.required &&
                      !input?.value &&
                      input.helperText) ||
                    (input.type === 'email' &&
                      !isEmailValid &&
                      input?.helperText) ||
                    (input.key === 'SIREN' &&
                      !isValidSIREN &&
                      input.helperText) ||
                    ''
                  }
                  onChange={(event) =>
                    handleChange(input.key, event.target.value)
                  }
                  onBlurCapture={() => {
                    if (input.key === 'SIREN') {
                      if (enseign.SIREN) handleSIRENBlur();
                      else setIsValidSIREN(true);
                    }
                  }}
                />
              )}
              {input?.type === 'tel' && (
                <CustomPhoneInput
                  label={input?.label}
                  required={input?.required}
                  value={input?.value}
                  onChange={(value: any) => handleChange(input?.key, value)}
                  error={
                    (isFinishFillInputs && input?.required && !input?.value) ||
                    (isFinishFillInputs &&
                      !!input?.value &&
                      !isValidPhoneNumber(input?.value as string))
                  }
                  helperText={
                    (isFinishFillInputs &&
                      !input?.value &&
                      input?.required &&
                      input.helperText) ||
                    (isFinishFillInputs &&
                      input?.value &&
                      !isValidPhoneNumber(input?.value as string) &&
                      'Le numéro de téléphone est invalide') ||
                    ''
                  }
                />
              )}
              {input.type === 'select' && (
                <CustomSelect
                  required={input.required}
                  errorSelect={
                    isFinishFillInputs && input?.required && !input?.value
                  }
                  helperText={
                    (isFinishFillInputs &&
                      input?.required &&
                      !input?.value &&
                      input?.helperText) ||
                    ''
                  }
                  label={input.label}
                  value={input.value}
                  options={
                    input?.key === 'type' ? typeOptions : PhysicalModelOptions
                  }
                  onChange={(e) =>
                    handleChange(input.key, e.target.value as string)
                  }
                />
              )}
            </Grid>
          );
        })}
      </Grid>
    </CustomDialog>
  );
};

const generateInputs = (enseign: CreateEnseigneInput): InputType[] => {
  return [
    {
      type: 'text',
      label: 'Nom',
      value: enseign.name,
      key: 'name',
      required: true,
      helperText: "Veuillez entrer le nom de l'enseigne",
    },
    {
      type: 'select',
      label: 'Type',
      value: enseign.type,
      key: 'type',
      required: true,
    },
    {
      type: 'select',
      label: 'Modèle physique',
      value: enseign.physicalModel,
      key: 'physicalModel',
      // required: true,
    },
    {
      type: 'text',
      label: 'Raison sociale',
      value: enseign.socialReason,
      key: 'socialReason',
      // required: true,
      // helperText: "Veuillez entrer le nom de l'enseigne",
    },
    {
      type: 'text',
      label: 'Adresse',
      value: enseign.address,
      key: 'address',
      // helperText: 'Veuillez entrer une ville',
    },
    {
      type: 'text',
      label: 'Code postal',
      value: enseign.zipCode,
      key: 'zipCode',
      required: false,
      helperText: 'Veuillez entrer un code postal',
    },
    {
      type: 'text',
      label: 'Ville',
      value: enseign.city,
      key: 'city',
      required: false,
      helperText: 'Veuillez entrer une ville',
    },
    {
      type: 'email',
      label: 'Email',
      value: enseign.email,
      key: 'email',
      // required: true,
      helperText: 'Veuillez entrer une adresse email valide',
    },
    {
      type: 'tel',
      label: 'Téléphone',
      value: enseign.phone,
      key: 'phone',
      // required: true,
      helperText: 'Veuillez entrer un numéro de téléphone',
    },

    {
      type: 'text',
      label: 'SIREN',
      value: enseign.SIREN,
      key: 'SIREN',
      helperText: 'Numéro de SIREN invalide',
    },
    { type: 'text', label: 'IBAN', value: enseign.IBAN, key: 'IBAN' },
  ];
};

const generateInitialEnseigne = (
  enseign: AllEnseignes_allEnseignes_enseignes,
): EnseigneStateType => {
  return {
    BIC: enseign?.BIC,
    IBAN: enseign?.IBAN,
    SIREN: enseign?.SIREN,
    city: enseign?.city,
    email: enseign?.email,
    name: enseign?.name,
    phone: enseign?.phone,
    type: TypeEnseigne[enseign?.type as TypeEnseigne],
    zipCode: enseign?.zipCode,
    country: enseign?.country,
    address: enseign?.address,
    physicalModel: enseign?.physicalModel,
    socialReason: enseign?.socialReason,
    entityIds:
      enseign.entities?.reduce(
        (acc, entity) =>
          entity?.echelon?.priority === 8 ? [...acc, entity.id] : acc,
        [] as number[],
      ) || [],
  };
};

export default EnseigneFormsModal;
